import {
  AddressElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js"
import { useState } from "react"
import { useCreatePaymentIntentMutation, useFinalizeTopUpMutation } from "./api"
import type { TopUpAction } from "./model"
import { useT } from "@/i18n"
import { Button } from "@/ui/components/ui/button"
import { Input } from "@/ui/components/ui/input"
import { Label } from "@/ui/components/ui/label"
import { Spinner } from "@/ui/components/ui/spinner"

type Props = {
  readonly credits: number
  readonly payLabel: string
  readonly action: TopUpAction
  /** Called with the paid PaymentIntent so TopUp can await its settlement. */
  readonly onSubmitted: (paymentIntentId: string) => void
}

/**
 * First purchase: collect a card + billing address with the Payment Element,
 * then create the PaymentIntent and confirm on-session (SCA only when the bank
 * requires it). Finalize eagerly settles so credits land without waiting on the
 * webhook; the outcome itself arrives over realtime, which TopUp awaits once
 * this hands off the paid PaymentIntent.
 */
export default function FirstPurchaseForm({
  credits,
  payLabel,
  action,
  onSubmitted,
}: Props) {
  const { t } = useT("money")
  const stripe = useStripe()
  const elements = useElements()
  const [createPaymentIntent] = useCreatePaymentIntentMutation()
  const [finalizeTopUp] = useFinalizeTopUpMutation()
  const [businessName, setBusinessName] = useState("")
  const [taxId, setTaxId] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const pay = async () => {
    if (!stripe || !elements) {
      return
    }

    setError(null)
    setBusy(true)
    try {
      const submitResult = await elements.submit()
      if (submitResult.error) {
        setError(submitResult.error.message ?? t("topUp.genericError"))

        return
      }

      const { clientSecret, paymentIntentId } = await createPaymentIntent({
        credits,
        action,
        taxId: taxId.trim() || null,
        businessName: businessName.trim() || null,
      }).unwrap()

      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}${window.location.pathname}`,
        },
        redirect: "if_required",
      })
      if (confirmError) {
        setError(confirmError.message ?? t("topUp.genericError"))

        return
      }

      await finalizeTopUp({ paymentIntentId }).unwrap()
      onSubmitted(paymentIntentId)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <PaymentElement />
      <AddressElement options={{ mode: "billing" }} />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="businessName">
          {t("paymentMethod.businessNameLabel")}
        </Label>
        <Input
          id="businessName"
          value={businessName}
          onChange={(e) => {
            setBusinessName(e.target.value)
          }}
          placeholder={t("paymentMethod.businessNamePlaceholder")}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="taxId">{t("paymentMethod.taxIdLabel")}</Label>
        <Input
          id="taxId"
          value={taxId}
          onChange={(e) => {
            setTaxId(e.target.value)
          }}
          placeholder={t("paymentMethod.taxIdPlaceholder")}
        />
      </div>
      {!!error && <p className="text-sm text-destructive">{error}</p>}
      <Button onClick={pay} disabled={!stripe || busy}>
        {busy && <Spinner />}
        {payLabel}
      </Button>
    </div>
  )
}
